import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { LastUpdatedOption } from '../../hooks/useDeviceRPC'

export type ColumnSelection = {
  /**
   * The id of the column
   */
  id: string
  /**
   * The label of the column
   */
  label?: string
  /**
   * Whether the column is visible
   */
  visible: boolean
}

export const defaultColumnConfig: ColumnSelection[] = [
  { id: 'lastSeen', label: 'Last seen', visible: true },
  { id: 'deviceName', label: 'Device name', visible: true },
  { id: 'sdkInfo', label: 'SDK info', visible: true },
  { id: 'peerKey', label: 'Peer key', visible: true },
  { id: 'dataRequested', label: 'Data requests', visible: true },
]

export type DevicesTableStore = {
  /**
   * The configuration for the columns in the table
   */
  columnConfig: ColumnSelection[]
  /**
   * A setter for the column configuration
   */
  setColumnConfig: (columnConfig: ColumnSelection[]) => void
  /**
   * The current last updated filter value
   */
  lastUpdatedFilter: LastUpdatedOption
  /**
   * Set the last updated filter
   */
  setLastUpdatedFilter: (filter: LastUpdatedOption) => void
  /**
   * The current page size for pagination
   */
  pageSize: number
  /**
   * Set the page size
   */
  setPageSize: (pageSize: number) => void
}

/**
 * A store for managing the configuration of the devices table, primarily the column configuration
 */
export const useDevicesTableStore = create<DevicesTableStore>()(
  persist(
    (set) => ({
      columnConfig: defaultColumnConfig,
      setColumnConfig: (columnConfig) => set({ columnConfig }),
      lastUpdatedFilter: '1 day',
      setLastUpdatedFilter: (lastUpdatedFilter) => set({ lastUpdatedFilter }),
      pageSize: 25,
      setPageSize: (pageSize) => set({ pageSize }),
    }),
    {
      name: 'devices-table-config',
      version: 3,
    },
  ),
)

export const isDefaultColumn = (column: ColumnSelection) =>
  defaultColumnConfig.some((defaultColumn) => defaultColumn.id === column.id)
